/**
 * A tiny history-based router (no library). Routes are matched in App.tsx with `matchRoute`;
 * anywhere else use `navigate()`, `<Link>` and `useLocation()`.
 * Pages that must finish something when left (the editor's autosave) do it in an effect cleanup.
 */
import { useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react';

export interface Location {
  pathname: string;
  search: string;
}

const listeners = new Set<() => void>();
let current: Location = read();

function read(): Location {
  return typeof window === 'undefined'
    ? { pathname: '/', search: '' }
    : { pathname: window.location.pathname, search: window.location.search };
}

function emit(): void {
  const next = read();
  if (next.pathname === current.pathname && next.search === current.search) return;
  current = next;
  for (const l of listeners) l();
}

if (typeof window !== 'undefined') window.addEventListener('popstate', emit);

/** Goes to `to` (a path like "/plan/abc"); `replace` swaps the current history entry instead. */
export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (opts.replace) window.history.replaceState(null, '', to);
  else window.history.pushState(null, '', to);
  window.scrollTo(0, 0);
  emit();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** React hook: the current path and query string. */
export function useLocation(): Location {
  return useSyncExternalStore(subscribe, () => current, () => current);
}

/**
 * Matches a path against a pattern like "/plan/:id". Returns the params, or null when it doesn't
 * match. Trailing slashes are ignored.
 */
export function matchRoute(pattern: string, pathname: string): Record<string, string> | null {
  const split = (s: string) => s.split('/').filter(Boolean);
  const p = split(pattern);
  const a = split(pathname);
  if (p.length !== a.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    const seg = p[i]!;
    const val = a[i]!;
    if (seg.startsWith(':')) {
      try {
        params[seg.slice(1)] = decodeURIComponent(val);
      } catch {
        return null;
      }
    } else if (seg !== val) return null;
  }
  return params;
}

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & { to: string; children: ReactNode };

/** An <a> that navigates without reloading (ctrl/middle-click still open a new tab). */
export function Link({ to, onClick, children, ...rest }: LinkProps) {
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(to);
  };
  return (
    <a href={to} onClick={handle} {...rest}>
      {children}
    </a>
  );
}
